/**
 * Lookthrough ECharts Adapter
 *
 * Transforms lookthrough data into ECharts treemap options.
 * Displays hierarchical portfolio data with categories and sub-categories.
 */

import type { EChartsCoreOption } from "echarts";
import type {
  LookthroughAdapterProps,
  EChartAdapterResult,
  LookthroughData,
} from "./types";

/**
 * Transform lookthrough API response into treemap data
 */
function transformLookthroughData(
  rawData: any[],
  colorPalette: string[]
): Array<{
  name: string;
  value: number;
  children?: Array<{ name: string; value: number; children?: Array<{ name: string; value: number }> }>;
  itemStyle?: { color: string };
}> {
  if (!rawData || rawData.length === 0) {
    return [];
  }

  // The first item should be the lookthrough response
  const apiResponse = rawData[0] as LookthroughData;

  if (!apiResponse || typeof apiResponse !== "object") {
    return [];
  }

  const treemapData: Array<{
    name: string;
    value: number;
    children?: Array<{ name: string; value: number; children?: Array<{ name: string; value: number }> }>;
    itemStyle?: { color: string };
  }> = [];

  // Process each portfolio in the response
  Object.entries(apiResponse).forEach(([portfolioKey, portfolioData]) => {
    // Skip metadata and non-portfolio entries
    if (
      portfolioKey === "metadata" ||
      portfolioKey.startsWith("_") ||
      !portfolioData ||
      typeof portfolioData !== "object"
    ) {
      return;
    }

    // Check if this is a portfolio with categories
    if ("categories" in portfolioData && Array.isArray(portfolioData.categories)) {
      const categories = portfolioData.categories;

      categories.forEach((category, catIndex) => {
        const categoryNode: any = {
          name: category.category_name,
          value: category.category_weight * 100, // Convert to percentage
          itemStyle: {
            color: colorPalette[catIndex % colorPalette.length],
          },
          children: [],
        };

        // Add sub-categories if they exist
        if (category.sub_categories && Array.isArray(category.sub_categories)) {
          category.sub_categories.forEach((subCat) => {
            const subCatNode: any = {
              name: subCat.sub_category_name,
              value: subCat.sub_category_weight * 100,
              children: [],
            };

            // Add holdings if they exist
            if (subCat.holdings && Array.isArray(subCat.holdings)) {
              subCat.holdings.forEach((holding) => {
                subCatNode.children.push({
                  name: holding.name,
                  value: holding.weight * 100,
                });
              });
            }

            // Only add sub-category if it has children or a positive value
            if (subCatNode.children.length > 0 || subCatNode.value > 0) {
              categoryNode.children.push(subCatNode);
            }
          });
        }

        // Only add category if it has children or a positive value
        if (categoryNode.children.length > 0 || categoryNode.value > 0) {
          treemapData.push(categoryNode);
        }
      });

      // Add unmapped holdings as a separate category if they exist
      if (portfolioData.unmapped_holdings && portfolioData.unmapped_holdings.length > 0) {
        const unmappedNode: any = {
          name: "Unmapped",
          value: (portfolioData.unmapped_weight || 0) * 100,
          itemStyle: {
            color: "#9ca3af", // Gray for unmapped
          },
          children: portfolioData.unmapped_holdings.map((holding) => ({
            name: holding.name,
            value: holding.weight * 100,
          })),
        };
        treemapData.push(unmappedNode);
      }
    }
  });

  return treemapData;
}

/**
 * Build ECharts option for lookthrough treemap
 */
export function buildLookthroughOption(
  props: LookthroughAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    colorPalette,
    chartSettings,
    visualSettings,
    isDarkMode,
  } = props;

  const showTooltips = visualSettings.showTooltips ?? true;
  const showPercentage = chartSettings.showPercentage ?? true;
  const precision = chartSettings.precision ?? 1;

  // Transform data to treemap format
  const treemapData = transformLookthroughData(rawData, colorPalette);

  // Handle empty data
  if (treemapData.length === 0) {
    return {
      option: {},
      chartType: "treemap",
      hasData: false,
    };
  }

  // Theme colors
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const borderColor = isDarkMode ? "#1e293b" : "#ffffff";

  // Build the ECharts option
  const option: EChartsCoreOption = {
    backgroundColor: "transparent",
    tooltip: showTooltips
      ? {
          trigger: "item",
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            const name = params.name;
            const value = params.value;
            const percentDisplay = showPercentage
              ? `${value.toFixed(precision)}%`
              : value.toFixed(precision);

            return `
              <div style="font-weight: 600; margin-bottom: 4px;">${name}</div>
              <div>Weight: <strong>${percentDisplay}</strong></div>
            `;
          },
        }
      : undefined,
    series: [
      {
        type: "treemap",
        data: treemapData,
        width: "95%",
        height: "90%",
        left: "center",
        top: "5%",
        roam: false,
        nodeClick: "zoomToNode",
        breadcrumb: {
          show: true,
          left: "center",
          bottom: 0,
          itemStyle: {
            color: isDarkMode ? "#334155" : "#f1f5f9",
            borderColor: isDarkMode ? "#475569" : "#e2e8f0",
            textStyle: {
              color: textColor,
            },
          },
          emphasis: {
            itemStyle: {
              color: isDarkMode ? "#475569" : "#e2e8f0",
            },
          },
        },
        label: {
          show: true,
          color: "#ffffff",
          fontSize: 12,
          fontWeight: "bold",
          textShadowColor: "rgba(0, 0, 0, 0.3)",
          textShadowBlur: 2,
          formatter: (params: any) => {
            const value = params.value;
            if (showPercentage && value > 2) {
              return `${params.name}\n${value.toFixed(precision)}%`;
            }
            return params.name;
          },
        },
        upperLabel: {
          show: true,
          height: 20,
          color: textColor,
          backgroundColor: "transparent",
        },
        itemStyle: {
          borderColor,
          borderWidth: 1,
          gapWidth: 2,
        },
        levels: [
          {
            // Level 0 - root level (categories)
            itemStyle: {
              borderWidth: 2,
              borderColor: borderColor,
              gapWidth: 3,
            },
            upperLabel: {
              show: false,
            },
          },
          {
            // Level 1 - sub-categories
            itemStyle: {
              borderWidth: 1,
              borderColor: borderColor,
              gapWidth: 2,
            },
            colorSaturation: [0.4, 0.7],
            upperLabel: {
              show: true,
              height: 20,
            },
          },
          {
            // Level 2 - holdings
            itemStyle: {
              borderWidth: 0.5,
              borderColor: borderColor,
              gapWidth: 1,
            },
            colorSaturation: [0.2, 0.5],
            label: {
              show: true,
              fontSize: 10,
            },
          },
        ],
      },
    ],
    color: colorPalette,
  };

  return {
    option,
    chartType: "treemap",
    hasData: true,
  };
}
